import moment from 'moment';
import Swal from 'sweetalert2';
import HelperMixin from '../../Shared/HelperMixin.vue';
import PaginationHelper from '../../Shared/PaginationHelper.vue';
import { printDispatchDocument } from './printDispatchDocuments';

const SOURCE_OPTIONS = [
    { value: 1, label: 'يدوي', icon: 'ti-edit' },
    { value: 2, label: 'من طلب معتمد', icon: 'ti-clipboard-check' },
    { value: 3, label: 'مباشر (طوارئ)', icon: 'ti-emergency-bed' },
];

const STATUS_OPTIONS = [
    { value: 1, label: 'مسودة', tone: 'secondary' },
    { value: 2, label: 'مطبوع — في انتظار الاستلام', tone: 'info' },
    { value: 3, label: 'مستلم — في انتظار سند الدخول', tone: 'warning' },
    { value: 4, label: 'مكتمل', tone: 'success' },
    { value: 9, label: 'ملغي', tone: 'danger' },
];

const STATUS_DRAFT = 1;
const STATUS_ISSUE_PRINTED = 2;
const STATUS_FINAL_PRINTED = 3;
const STATUS_COMPLETED = 4;
const STATUS_CANCELLED = 9;

const STEPS = [
    { key: 'draft', title: 'إنشاء عملية صرف', short: 'مسودة', icon: 'ti-file-plus' },
    { key: 'issue', title: 'طباعة سند الصرف', short: 'سند الصرف', icon: 'ti-printer' },
    { key: 'review', title: 'مراجعة المستلم وموافقته', short: 'موافقة المستلم', icon: 'ti-check' },
    { key: 'finalInvoice', title: 'طباعة الفاتورة النهائية', short: 'فاتورة نهائية', icon: 'ti-receipt' },
    { key: 'receipt', title: 'تأكيد الاستلام وسند الدخول', short: 'سند الدخول', icon: 'ti-package' },
];

export default {
    name: 'DispatchManagement',
    mixins: [HelperMixin],
    components: { PaginationHelper },

    async created() {
        await this.CheckLoginStatus();
        if (!this.canViewPage) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'ليس لديك صلاحية للوصول إلى الصرف');
            this.href('/Home');
            return;
        }
        await Promise.all([this.loadBranches(), this.loadStockings(), this.loadProducts()]);
        await this.GetStatistics();
        this.GetInfo(1);
    },

    computed: {
        canViewPage() {
            if (!this.loginDetails) return false;
            if (this.hasInventoryAndStockAccessUi()) return true;
            if (this.isMinistryPharmacyDepartmentRole()) return true;
            if (this.isFacilityCenterRole() && this.loginDetails.branchesId) return true;
            return false;
        },
        canManage() {
            return this.hasInventoryAndStockAccessUi();
        },
        canConfirmReceipt() {
            return this.isFacilityCenterRole() && !!this.loginDetails && !!this.loginDetails.branchesId;
        },
        statsCards() {
            const s = this.Statistics || {};
            return [
                this.statCard('عمليات صرف قيد التنفيذ', s.inProgress, 'warning', 'ti ti-clock'),
                this.statCard('عمليات صرف مكتملة', s.completed, 'success', 'ti ti-circle-check'),
                this.statCard('عمليات صرف ملغية', s.cancelled, 'danger', 'ti ti-circle-x'),
                this.statCard('إجمالي الكمية المصروفة (الشهر)', s.monthlyQuantity, 'info', 'ti ti-package'),
            ];
        },
        sourceOptions() { return SOURCE_OPTIONS; },
        statusOptions() { return STATUS_OPTIONS; },
        steps() { return STEPS; },
        ministryStockings() {
            return (this.stockingsList || []).filter(s => !s.branchesId && s.status === 1);
        },
        facilityBranches() {
            return (this.branchesList || []).filter(b => b.status !== 9);
        },
        wizardLabel() {
            const t = this.wizardSourceType;
            const opt = SOURCE_OPTIONS.find(x => x.value === t);
            return opt ? opt.label : '—';
        },
        modalLinesTotal() {
            return (this.modalLines || []).reduce((s, l) => s + (Number(l.Quantity) || 0), 0);
        },
        statusBadgeClass() {
            return (status) => {
                const opt = STATUS_OPTIONS.find(x => x.value === Number(status));
                return opt ? `badge bg-label-${opt.tone}` : 'badge bg-label-secondary';
            };
        },
        statusLabelOf() {
            return (status) => {
                const opt = STATUS_OPTIONS.find(x => x.value === Number(status));
                return opt ? opt.label : '—';
            };
        },
        sourceChipClass() {
            return (t) => {
                if (Number(t) === 2) return 'source-chip source-fromorder';
                if (Number(t) === 3) return 'source-chip source-direct';
                return 'source-chip';
            };
        },
        sourceLabelOf() {
            return (t) => {
                const opt = SOURCE_OPTIONS.find(x => x.value === Number(t));
                return opt ? opt.label : '—';
            };
        },
    },

    data() {
        return {
            ScreenTitle: 'الصرف (Dispatch)',
            pageNo: 1,
            pageSize: 10,
            pages: 0,

            FilterFacility: '',
            FilterStatus: '',
            FilterFrom: '',
            FilterTo: '',
            Search: '',

            Info: [],
            Statistics: {},

            branchesList: [],
            stockingsList: [],
            productsList: [],
            approvedOrders: [],
            approvedOrdersLoading: false,

            wizardSourceType: 1,

            modalSourceStockingId: '',
            modalFacilityBranchesId: '',
            modalNote: '',
            modalLines: [],
            modalAttachments: [],
            modalApprovedOrderId: '',
            submitting: false,

            currentDetail: null,
            detailLoading: false,
        };
    },

    filters: {
        moment(d) { return d ? moment(d).format('DD/MM/YYYY') : '—'; },
        momentTime(d) { return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—'; },
    },

    methods: {
        tableRowNumber(i) {
            return ((Number(this.pageNo) - 1) * Number(this.pageSize)) + i + 1;
        },

        extractError(err) {
            if (err && err.response && err.response.data) {
                return typeof err.response.data === 'string' ? err.response.data : (err.response.data.message || 'حدث خطأ غير متوقع');
            }
            return (err && err.message) || 'حدث خطأ غير متوقع';
        },

        async loadBranches() {
            try {
                const res = await this.$http.GetAllBranches();
                this.branchesList = (res && res.data && res.data.info) || [];
            } catch { this.branchesList = []; }
        },

        async loadStockings() {
            try {
                const res = await this.$http.GetAllStocking();
                const arr = (res && res.data && res.data.info) || (res && res.data) || [];
                this.stockingsList = Array.isArray(arr) ? arr : [];
            } catch { this.stockingsList = []; }
        },

        async loadProducts() {
            try {
                const res = await this.$http.GetAllProducts();
                const arr = (res && res.data && res.data.info) || (res && res.data) || [];
                this.productsList = Array.isArray(arr) ? arr : [];
            } catch { this.productsList = []; }
        },

        async loadApprovedOrders() {
            this.approvedOrdersLoading = true;
            try {
                const res = await this.$http.GetDispatchApprovedOrders();
                this.approvedOrders = (res && res.data) || [];
            } catch { this.approvedOrders = []; } finally { this.approvedOrdersLoading = false; }
        },

        ApplyFilters() { this.GetInfo(1); },

        ResetFilters() {
            this.FilterFacility = '';
            this.FilterStatus = '';
            this.FilterFrom = '';
            this.FilterTo = '';
            this.Search = '';
            this.GetInfo(1);
        },

        GetStatistics() {
            return this.$http.GetDispatchStatistics()
                .then(res => { this.Statistics = (res && res.data) || {}; })
                .catch(() => { this.Statistics = {}; });
        },

        GetInfo(pageNo) {
            this.pageNo = pageNo || this.pageNo || 1;
            if (!this.Search) this.$blockUI.Start();
            this.$http.GetDispatch(
                this.pageNo,
                this.pageSize,
                this.Search,
                this.FilterFacility || null,
                this.FilterStatus === '' ? null : Number(this.FilterStatus),
                this.FilterFrom || null,
                this.FilterTo || null,
            ).then(res => {
                if (!this.Search) this.$blockUI.Stop();
                this.Info = (res.data && res.data.info) || [];
                this.pages = (res.data && res.data.count) || 0;
            }).catch(() => {
                if (!this.Search) this.$blockUI.Stop();
                this.Info = [];
                this.pages = 0;
            });
        },

        OpenWizard() {
            this.wizardSourceType = 1;
            this.modalSourceStockingId = '';
            this.modalFacilityBranchesId = '';
            this.modalNote = '';
            this.modalLines = [this.buildEmptyLine()];
            this.modalAttachments = [];
            this.modalApprovedOrderId = '';
            this.approvedOrders = [];
        },

        async OnWizardContinue() {
            if (!this.wizardSourceType) return;
            const elClose = document.getElementById('CloseDispatchWizardModal');
            if (this.wizardSourceType === 2) {
                await this.loadApprovedOrders();
            }
            if (elClose) elClose.click();
            this.$nextTick(() => {
                const formId = this.wizardSourceType === 2
                    ? 'DispatchFromOrderModal'
                    : (this.wizardSourceType === 3 ? 'DispatchDirectModal' : 'DispatchManualModal');
                const trigger = document.getElementById(formId + 'Btn');
                if (trigger) trigger.click();
            });
        },

        buildEmptyLine() {
            return {
                ProductsId: '',
                SerialNumber: '',
                ExpiryDate: '',
                Quantity: 0,
                UnitPrice: '',
            };
        },

        AddLine() {
            this.modalLines.push(this.buildEmptyLine());
        },

        RemoveLine(idx) {
            this.modalLines.splice(idx, 1);
            if (this.modalLines.length === 0) this.modalLines.push(this.buildEmptyLine());
        },

        OnAttachmentChange(event) {
            const files = (event && event.target && event.target.files) || [];
            const collected = [];
            const readers = [];
            for (let i = 0; i < files.length; i++) {
                const f = files[i];
                readers.push(new Promise((resolve) => {
                    const r = new FileReader();
                    r.onload = () => {
                        collected.push({ FileName: f.name, ContentType: f.type, Base64Content: r.result });
                        resolve();
                    };
                    r.onerror = resolve;
                    r.readAsDataURL(f);
                }));
            }
            Promise.all(readers).then(() => {
                this.modalAttachments = this.modalAttachments.concat(collected);
            });
        },

        RemoveAttachment(idx) {
            this.modalAttachments.splice(idx, 1);
        },

        validateModalLines() {
            if (!this.modalLines || this.modalLines.length === 0) return 'الرجاء إضافة صنف واحد على الأقل.';
            for (const ln of this.modalLines) {
                if (!ln.ProductsId) return 'الرجاء اختيار جميع الأصناف.';
                if (!ln.Quantity || Number(ln.Quantity) <= 0) return 'الرجاء إدخال كمية صحيحة لكل صنف.';
            }
            return null;
        },

        buildLinesPayload() {
            return this.modalLines.map(ln => ({
                ProductsId: Number(ln.ProductsId),
                Quantity: Number(ln.Quantity),
                UnitPrice: ln.UnitPrice === '' || ln.UnitPrice == null ? null : Number(ln.UnitPrice),
                SerialNumber: ln.SerialNumber || null,
                ExpiryDate: ln.ExpiryDate || null,
            }));
        },

        SubmitManual() {
            if (!this.modalSourceStockingId || !this.modalFacilityBranchesId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'الرجاء اختيار المخزن المصدر والجهة المستفيدة');
                return;
            }
            const err = this.validateModalLines();
            if (err) { this.$helper.ShowMessage('warning', 'تنبيه', err); return; }
            const payload = {
                SourceStockingId: Number(this.modalSourceStockingId),
                FacilityBranchesId: Number(this.modalFacilityBranchesId),
                Note: this.modalNote || '',
                Lines: this.buildLinesPayload(),
            };
            this.submitting = true;
            this.$blockUI.Start();
            this.$http.DispatchCreateManual(payload)
                .then(() => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('success', 'نجاح', 'تم إنشاء الصرف اليدوي');
                    this.$helper.DestroyElemntById('CloseDispatchManualModal');
                    this.GetInfo(1); this.GetStatistics();
                })
                .catch(err => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                });
        },

        SubmitDirect() {
            if (!this.modalSourceStockingId || !this.modalFacilityBranchesId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'الرجاء اختيار المخزن المصدر والجهة المستفيدة');
                return;
            }
            if (!this.modalAttachments.length) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'الصرف المباشر يتطلب رفع المستندات التعزيزية.');
                return;
            }
            const err = this.validateModalLines();
            if (err) { this.$helper.ShowMessage('warning', 'تنبيه', err); return; }
            const payload = {
                SourceStockingId: Number(this.modalSourceStockingId),
                FacilityBranchesId: Number(this.modalFacilityBranchesId),
                Note: this.modalNote || '',
                Lines: this.buildLinesPayload(),
                Attachments: this.modalAttachments,
            };
            this.submitting = true;
            this.$blockUI.Start();
            this.$http.DispatchCreateDirect(payload)
                .then(() => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('success', 'نجاح', 'تم إنشاء الصرف المباشر');
                    this.$helper.DestroyElemntById('CloseDispatchDirectModal');
                    this.GetInfo(1); this.GetStatistics();
                })
                .catch(err => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                });
        },

        SubmitFromOrder() {
            if (!this.modalApprovedOrderId) {
                this.$helper.ShowMessage('warning', 'تنبيه', 'الرجاء اختيار طلب معتمد');
                return;
            }
            this.submitting = true;
            this.$blockUI.Start();
            this.$http.DispatchCreateFromOrder(this.modalApprovedOrderId)
                .then(() => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('success', 'نجاح', 'تم إنشاء صرف من الطلب المعتمد');
                    this.$helper.DestroyElemntById('CloseDispatchFromOrderModal');
                    this.GetInfo(1); this.GetStatistics();
                })
                .catch(err => {
                    this.$blockUI.Stop(); this.submitting = false;
                    this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                });
        },

        async OpenDetail(item) {
            this.currentDetail = null;
            this.detailLoading = true;
            try {
                const res = await this.$http.GetDispatchOne(item.id);
                this.currentDetail = (res && res.data) || null;
            } catch (err) {
                this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
            } finally {
                this.detailLoading = false;
            }
        },

        stepStateClass(stepKey) {
            const detail = this.currentDetail;
            if (!detail) return '';
            const status = Number(detail.status || 0);
            if (status === STATUS_CANCELLED) return '';
            const stepRank = STEPS.findIndex(s => s.key === stepKey);
            const statusRank = (() => {
                if (status === STATUS_DRAFT) return 0;
                if (status === STATUS_ISSUE_PRINTED) return 2;
                if (status === STATUS_FINAL_PRINTED) return 3;
                if (status === STATUS_COMPLETED) return 4;
                return 0;
            })();
            if (stepRank < statusRank) return 'is-done';
            if (stepRank === statusRank) return 'is-active';
            return '';
        },

        stepDate(stepKey) {
            const d = this.currentDetail;
            if (!d) return '';
            switch (stepKey) {
                case 'draft': return d.createdOn ? moment(d.createdOn).format('DD/MM/YYYY HH:mm') : '';
                case 'issue': return d.issueVoucherPrintedOn ? moment(d.issueVoucherPrintedOn).format('DD/MM/YYYY HH:mm') : '';
                case 'finalInvoice': return d.finalInvoicePrintedOn ? moment(d.finalInvoicePrintedOn).format('DD/MM/YYYY HH:mm') : '';
                case 'receipt': return d.receiptVoucherPrintedOn ? moment(d.receiptVoucherPrintedOn).format('DD/MM/YYYY HH:mm') : '';
                default: return '';
            }
        },

        PrintIssueVoucher(item) {
            Swal.fire({
                title: 'تأكيد طباعة سند الصرف',
                text: 'سيتم خصم الكميات من المخزن المصدر. هل تريد المتابعة؟',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد وطباعة',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchPrintIssueVoucher(item.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم خصم الكميات. سيتم فتح نافذة الطباعة.');
                        this.GetInfo(this.pageNo); this.GetStatistics();
                        const res = await this.$http.GetDispatchOne(item.id);
                        if (res && res.data) printDispatchDocument(res.data, 'issue', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        PrintFinalInvoice(item) {
            Swal.fire({
                title: 'تأكيد طباعة الفاتورة النهائية',
                text: 'سيتم تحديث الحالة إلى "في انتظار سند الدخول".',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-primary me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد وطباعة',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchPrintFinalInvoice(item.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم تحديث الحالة. سيتم فتح نافذة الطباعة.');
                        this.GetInfo(this.pageNo); this.GetStatistics();
                        const res = await this.$http.GetDispatchOne(item.id);
                        if (res && res.data) printDispatchDocument(res.data, 'finalInvoice', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        ConfirmReceipt(item) {
            Swal.fire({
                title: 'تأكيد الاستلام وسند الدخول',
                text: 'سيتم إيداع الكميات في مخزن المرفق وإكمال العملية.',
                icon: 'question',
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-success me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'تأكيد الاستلام',
                cancelButtonText: 'إلغاء',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchConfirmReceipt(item.id)
                    .then(async () => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم تأكيد الاستلام وإكمال العملية.');
                        this.GetInfo(this.pageNo); this.GetStatistics();
                        const res = await this.$http.GetDispatchOne(item.id);
                        if (res && res.data) printDispatchDocument(res.data, 'receipt', { logoUrl: this.logoPath });
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        CancelDispatch(item) {
            Swal.fire({
                title: 'إلغاء عملية الصرف',
                text: 'الرجاء إدخال سبب الإلغاء',
                input: 'textarea',
                inputAttributes: { 'aria-label': 'سبب الإلغاء' },
                showCancelButton: true,
                customClass: { confirmButton: 'btn btn-danger me-3', cancelButton: 'btn btn-label-secondary' },
                buttonsStyling: false,
                confirmButtonText: 'إلغاء العملية',
                cancelButtonText: 'تراجع',
            }).then(result => {
                if (!result.isConfirmed) return;
                this.$blockUI.Start();
                this.$http.DispatchCancel(item.id, result.value || '')
                    .then(() => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('success', 'نجاح', 'تم إلغاء العملية');
                        this.GetInfo(this.pageNo); this.GetStatistics();
                    })
                    .catch(err => {
                        this.$blockUI.Stop();
                        this.$helper.ShowMessage('error', 'خطأ', this.extractError(err));
                    });
            });
        },

        ReprintDocument(detail, docKey) {
            if (!detail) return;
            printDispatchDocument(detail, docKey, { logoUrl: this.logoPath });
        },
    },
};
